/**
 * Paragraph Wireframes — shared client-side enhancements.
 *
 * Currently provides:
 *   - Letter-grid ambient background for any element with `.pw-grid`.
 *     Builds a CSS grid of random letters, animates subtly when in view,
 *     respects prefers-reduced-motion.
 *
 *   Usage: place `<div class="pw-grid" aria-hidden="true"></div>` inside
 *   a relatively-positioned hero/closer container. This script auto-inits.
 *
 * Notes:
 *   - Decoupled from `assets/paragraph-hero-grid.js` (which is a Custom
 *     Element specific to the PDP `.paragraph-hero` section). Do not
 *     merge the two — the PDP grid has hero-specific knockout behavior
 *     we don't want on page templates.
 */
(function () {
  if (typeof window === 'undefined') return;

  var CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  /* Matches assets/paragraph-hero-grid.js so the pw page grid looks
     identical in density to the homepage hero grid. */
  var CELL = 48;

  function randInt(n) {
    return (Math.random() * n) | 0;
  }

  function randomChar() {
    return CHARS[randInt(CHARS.length)];
  }

  function buildGrid(el) {
    var rect = el.getBoundingClientRect();
    var cols = Math.max(14, Math.ceil(rect.width / CELL));
    var rows = Math.max(8, Math.ceil(rect.height / CELL));
    el.style.setProperty('--grid-cols', cols);

    var wrap = document.createElement('div');
    wrap.className = 'pw-grid__cells';

    var frag = document.createDocumentFragment();
    var total = cols * rows;
    var cells = [];

    for (var i = 0; i < total; i++) {
      var s = document.createElement('span');
      var r = Math.random();
      s.className =
        r < 0.45
          ? 'pw-grid__cell pw-grid__cell--emb'
          : r < 0.9
          ? 'pw-grid__cell pw-grid__cell--deb'
          : 'pw-grid__cell';
      s.textContent = randomChar();
      frag.appendChild(s);
      cells.push(s);
    }

    wrap.appendChild(frag);
    el.appendChild(wrap);
    return cells;
  }

  function startAnimation(cells) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

    var len = cells.length;
    var changes = Math.max(1, Math.ceil(len * 0.003));
    var rafId;

    function tick() {
      for (var i = 0; i < changes; i++) {
        cells[randInt(len)].textContent = randomChar();
      }
      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);
    return {
      stop: function () {
        if (rafId) cancelAnimationFrame(rafId);
      }
    };
  }

  function initPwGrid(el) {
    if (el.dataset.pwGridBuilt === '1') return;

    /* Wait for a non-zero width before building — otherwise we render
       into the `Math.max(14, ...)` floor and end up with big sparse cells.
       Common when the script runs before layout (ResizeObserver fires
       once layout is done). */
    var rect = el.getBoundingClientRect();
    if (rect.width < 50) {
      if (typeof ResizeObserver === 'undefined') return;
      var ro = new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var r = entries[i].contentRect;
          if (r.width >= 50) {
            ro.disconnect();
            initPwGrid(el);
            return;
          }
        }
      });
      ro.observe(el);
      return;
    }

    el.dataset.pwGridBuilt = '1';

    var cells = buildGrid(el);
    var anim = null;

    var io = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting && !anim) {
          anim = startAnimation(cells);
        } else if (!entries[0].isIntersecting && anim) {
          anim.stop();
          anim = null;
        }
      },
      { threshold: 0.05 }
    );
    io.observe(el);
  }

  function initAll() {
    document.querySelectorAll('.pw-grid').forEach(initPwGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on resize (window dimensions changed → rebuild cell count)
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll('.pw-grid').forEach(function (el) {
        var wrap = el.querySelector('.pw-grid__cells');
        if (wrap) wrap.remove();
        delete el.dataset.pwGridBuilt;
      });
      initAll();
    }, 150);
  });

  // Expose for Shopify's theme editor (re-init after section reload)
  if (typeof document !== 'undefined') {
    document.addEventListener('shopify:section:load', initAll);
    document.addEventListener('shopify:section:select', initAll);
  }
})();
